import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronDown, ChevronUp, ChevronLeft, MapPin, Trophy, Clock, Info } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ScoreDetailModal({ isOpen, onClose, gymnast, apparatus, score }) {
  if (!score) return null;
  const gymnastName = score?.gymnastName || gymnast?.gymnastName || gymnast?.preferredName || 
                      gymnast?.name || `${gymnast?.firstName || ''} ${gymnast?.lastName || ''}`.trim() || 'Unknown';
  const gymnastNumber = score?.gymnastNumber || gymnast?.gymnastNumber || gymnast?.number || '-';
  const clubName = score?.clubName || gymnast?.clubName || gymnast?.club || '-';
  const apparatusDisplay = (apparatus || score?.apparatus || '').toUpperCase();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xs p-0 overflow-hidden rounded-lg shadow-2xl border-0">
        <div className="bg-white">
          <div className="bg-white px-6 py-4 text-center border-b border-gray-200">
            <img src="/scorz-logo.png" alt="Scorz" className="h-10 mx-auto" />
            <p className="text-gray-800 text-sm font-semibold tracking-wider uppercase mt-2">{apparatusDisplay}</p>
          </div>
          <div className="px-6 py-4 border-b border-dashed border-gray-300 text-center">
            <p className="text-4xl font-bold text-black mb-1">{gymnastNumber}</p>
            <p className="text-lg font-semibold text-gray-900">{gymnastName}</p>
            <p className="text-sm text-gray-500">{clubName}</p>
          </div>
          <div className="px-6 py-4 space-y-3 font-mono text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">D Score</span>
              <span className="font-semibold text-gray-900">{parseFloat(score.difficulty || 0).toFixed(3)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">E Score</span>
              <span className="font-semibold text-gray-900">{parseFloat(score.execution || 0).toFixed(3)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Neutral Ded.</span>
              <span className="font-semibold text-red-600">-{parseFloat(score.neutralDeductions || 0).toFixed(3)}</span>
            </div>
            <div className="border-t border-gray-300 my-2"></div>
            <div className="flex justify-between items-center pt-1">
              <span className="text-gray-900 font-bold text-base">FINAL</span>
              <span className="font-bold text-2xl text-black">{parseFloat(score.finalScore || 0).toFixed(3)}</span>
            </div>
          </div>
          <div className="px-6 pb-4">
            <button onClick={() => onClose(false)} className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors">
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CoachesLiveScoresPage() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('rankings');
  
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedApparatus, setSelectedApparatus] = useState('vault');
  const [rankings, setRankings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(false);
  const [qualification, setQualification] = useState(null);
  
  const [latestScores, setLatestScores] = useState([]);
  const [loadingScores, setLoadingScores] = useState(false);
  
  const [clubs, setClubs] = useState([]);
  const [filterClub, setFilterClub] = useState('');
  
  const [scoreDetailOpen, setScoreDetailOpen] = useState(false);
  const [selectedGymnastDetail, setSelectedGymnastDetail] = useState(null);
  const [selectedScoreDetail, setSelectedScoreDetail] = useState(null);
  const [selectedApparatusForModal, setSelectedApparatusForModal] = useState(null);
  
  const refreshIntervalRef = useRef(null);
  const isInitialRankingsLoad = useRef(true);
  const isInitialScoresLoad = useRef(true);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      const fetchCategories = async () => {
        try {
          const res = await axios.get(`${API}/public/categories/${selectedEvent.id}`);
          setCategories(res.data.categories || []);
        } catch {
          setCategories(selectedEvent.categories || []);
        }
      };
      const fetchClubs = async () => {
        try {
          const res = await axios.get(`${API}/public/clubs/${selectedEvent.id}`);
          setClubs(res.data.clubs || []);
        } catch {
          setClubs([]);
        }
      };
      fetchCategories();
      fetchClubs();
      setSelectedCategory(null);
      setRankings([]);
      setLatestScores([]);
      setFilterClub('');
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (selectedEvent && selectedCategory && activeTab === 'rankings') {
      isInitialRankingsLoad.current = true;
      fetchRankings();
      refreshIntervalRef.current = setInterval(fetchRankings, 5000);
      return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
    }
  }, [selectedEvent, selectedCategory, selectedApparatus, activeTab]);

  useEffect(() => {
    if (selectedEvent && activeTab === 'scores') {
      isInitialScoresLoad.current = true;
      fetchLatestScores();
      refreshIntervalRef.current = setInterval(fetchLatestScores, 5000);
      return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
    }
  }, [selectedEvent, activeTab]);

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/public/competitions`);
      const comps = response.data.competitions || response.data || [];
      setCompetitions(Array.isArray(comps) ? comps : []);
    } catch { setCompetitions([]); }
    finally { setLoading(false); }
  };

  const fetchRankings = async () => {
    if (!selectedEvent || !selectedCategory) return;
    if (isInitialRankingsLoad.current) setLoadingRankings(true);
    try {
      const response = await axios.get(`${API}/public/rankings/${selectedEvent.id}`, {
        params: { category: selectedCategory, apparatus: selectedApparatus }
      });
      setRankings(response.data.rankings || []);
      setQualification(response.data.qualification || null);
    } catch { setRankings([]); }
    finally { setLoadingRankings(false); isInitialRankingsLoad.current = false; }
  };

  const fetchLatestScores = async () => {
    if (!selectedEvent) return;
    if (isInitialScoresLoad.current) setLoadingScores(true);
    try {
      const response = await axios.get(`${API}/public/latest-scores/${selectedEvent.id}`);
      setLatestScores(response.data.scores || []);
    } catch { setLatestScores([]); }
    finally { setLoadingScores(false); isInitialScoresLoad.current = false; }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const apparatusTabs = [
    { id: 'vault', label: 'Vault' }, { id: 'bars', label: 'Bars' },
    { id: 'beam', label: 'Beam' }, { id: 'floor', label: 'Floor' },
    { id: 'range', label: 'Range' }, { id: 'aa', label: 'AA' }
  ];

  const handleGymnastClick = (gymnast, score, apparatus = null) => {
    if (selectedApparatus === 'aa' && !apparatus) return;
    if (!score || score.didNotCompete || score.finalScore === null) return;
    setSelectedGymnastDetail(gymnast);
    setSelectedScoreDetail(score);
    setSelectedApparatusForModal(apparatus || selectedApparatus);
    setScoreDetailOpen(true);
  };

  const getScoreDisplay = (ranking) => {
    if (selectedApparatus === 'aa') {
      if (ranking.aaScore === null || ranking.aaScore === undefined) {
        return <span className="text-gray-400">Not yet competed</span>;
      }
      return (
        <div className="flex flex-col items-end">
          <span className="font-semibold flex items-center gap-2">
            {parseFloat(ranking.aaScore || 0).toFixed(3)}
            {ranking.qualified && (
              <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">Q</span>
            )}
          </span>
        </div>
      );
    }
    const score = ranking.score;
    if (!score) return <span className="text-gray-400">Not yet competed</span>;
    if (score.didNotCompete) return <span className="text-gray-400">Did not compete (0.000)</span>;
    if (score.finalScore === null || score.finalScore === undefined) return <span className="text-gray-400">Not yet competed</span>;
    return <span className="font-semibold">{parseFloat(score.finalScore || 0).toFixed(3)}</span>;
  };

  const categorizeCompetitions = () => {
    const now = new Date();
    const current = [], future = [], past = [];
    competitions.forEach(comp => {
      const startDate = new Date(comp.startsOn || comp.startDate);
      const endDate = new Date(comp.endsOn || comp.endDate || comp.startsOn || comp.startDate);
      endDate.setHours(23, 59, 59, 999);
      if (now >= startDate && now <= endDate) current.push(comp);
      else if (startDate > now) future.push(comp);
      else past.push(comp);
    });
    return { current, future, past };
  };

  const { current, future, past } = categorizeCompetitions();
  const [expandedSections, setExpandedSections] = useState({ current: true, future: false, past: false });
  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // Event detail view
  if (selectedEvent) {
    const venue = selectedEvent.publicVenue || selectedEvent.location || selectedEvent.venue || 'TBC';
    const startDate = selectedEvent.startsOn || selectedEvent.startDate;
    const endDate = selectedEvent.endsOn || selectedEvent.endDate || startDate;
    const displayDates = selectedEvent.publicDates || `${formatDate(startDate)} - ${formatDate(endDate)}`;

    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <div className="bg-black text-white text-center py-1.5 sm:py-2 px-4 text-xs sm:text-sm font-medium">
          Coaches View - Live Scores & Rankings
        </div>
        
        <header className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 cursor-pointer" onClick={() => setSelectedEvent(null)}>
              <img src="/scorz-logo.png" alt="Scorz" className="h-8 sm:h-12" />
            </div>
            <span className="text-xs sm:text-sm text-gray-500 font-medium">Coaches Access</span>
          </div>
          
          <div className="bg-gray-100 border-t">
            <div className="max-w-7xl mx-auto px-2 sm:px-4">
              <div className="flex gap-1 overflow-x-auto">
                {[
                  { id: 'info', label: 'Info', mobileLabel: 'Info', icon: Info },
                  { id: 'rankings', label: 'Live Rankings', mobileLabel: 'Rankings', icon: Trophy },
                  { id: 'scores', label: 'Latest Scores', mobileLabel: 'Scores', icon: Clock }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    data-testid={`coaches-tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.mobileLabel}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 w-full">
          {activeTab === 'info' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">Event Information</h2>
              <div className="space-y-6">
                <div><h3 className="font-semibold text-gray-800 mb-1">Event</h3><p className="text-gray-600">{selectedEvent.title}</p></div>
                <div><h3 className="font-semibold text-gray-800 mb-1">Venue</h3><p className="text-gray-600">{venue}</p></div>
                <div><h3 className="font-semibold text-gray-800 mb-1">Dates</h3><p className="text-gray-600">{displayDates}</p></div>
              </div>
            </div>
          )}

          {activeTab === 'rankings' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">Live Rankings</h2>
              
              {!selectedCategory ? (
                <div>
                  <p className="text-gray-500 mb-4">Select a category:</p>
                  {categories.length === 0 ? (
                    <p className="text-gray-400">No categories available for this event.</p>
                  ) : (
                    <ul className="space-y-2">
                      {categories.map((cat, index) => (
                        <li key={index}>
                          <button onClick={() => setSelectedCategory(cat)} className="text-left w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded transition-colors" data-testid={`coaches-category-${cat}`}>
                            {cat}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div>
                  <button onClick={() => setSelectedCategory(null)} className="flex items-center gap-1 text-gray-500 hover:text-black mb-4 transition-colors">
                    <ChevronLeft className="w-4 h-4" /> Back to categories
                  </button>
                  <h3 className="font-semibold text-gray-800 mb-4">{selectedCategory}</h3>
                  
                  <div className="flex flex-wrap gap-1 mb-3 sm:mb-4 -mx-1">
                    {apparatusTabs.map(tab => (
                      <button key={tab.id} onClick={() => setSelectedApparatus(tab.id)}
                        className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded transition-colors ${
                          selectedApparatus === tab.id ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`} data-testid={`coaches-apparatus-${tab.id}`}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-3 mb-4">
                    <select value={filterClub} onChange={(e) => setFilterClub(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value="">All Clubs</option>
                      {clubs.map((club) => (<option key={club.id} value={club.name}>{club.name}</option>))}
                    </select>
                    {filterClub && (<button onClick={() => setFilterClub('')} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline">Clear Filter</button>)}
                  </div>
                  
                  {selectedApparatus === 'aa' && qualification?.enabled && qualification?.text && (
                    <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">Q</span>
                      <span className="text-sm text-green-800">= Qualified for {qualification.text}</span>
                    </div>
                  )}
                  
                  {loadingRankings ? (
                    <p className="text-gray-500">Loading rankings...</p>
                  ) : rankings.length === 0 ? (
                    <p className="text-gray-400">No rankings available yet.</p>
                  ) : (
                    <div className="space-y-1">
                      <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-xs text-gray-500 font-medium border-b">
                        <div className="col-span-1">#</div><div className="col-span-1">No.</div>
                        <div className="col-span-4">Name</div><div className="col-span-3">Club</div>
                        <div className="col-span-3 text-right">Score</div>
                      </div>
                      <div className="sm:hidden grid grid-cols-12 gap-1 px-2 py-2 text-xs text-gray-500 font-medium border-b">
                        <div className="col-span-2">#</div><div className="col-span-6">Gymnast</div>
                        <div className="col-span-4 text-right">Score</div>
                      </div>
                      {rankings.filter(r => !filterClub || r.club === filterClub).map((ranking, idx) => {
                        const position = ranking.rank || (idx + 1);
                        const getMedalStyle = () => {
                          if (position === 1) return 'bg-yellow-400 text-yellow-900';
                          if (position === 2) return 'bg-gray-300 text-gray-700';
                          if (position === 3) return 'bg-amber-600 text-amber-100';
                          return 'bg-gray-100 text-gray-500';
                        };
                        return (
                          <React.Fragment key={ranking.gymnastId || idx}>
                            <div className={`hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-sm hover:bg-gray-50 rounded ${selectedApparatus !== 'aa' && ranking.score && !ranking.score.didNotCompete && ranking.score.finalScore !== null ? 'cursor-pointer' : ''}`} onClick={() => handleGymnastClick(ranking, ranking.score)}>
                              <div className="col-span-1"><span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${getMedalStyle()}`}>{position}</span></div>
                              <div className="col-span-1 font-medium">{ranking.number || '-'}</div>
                              <div className="col-span-4">{ranking.name}</div>
                              <div className="col-span-3 text-gray-600 truncate">{ranking.club}</div>
                              <div className="col-span-3 text-right">{getScoreDisplay(ranking)}</div>
                            </div>
                            <div className={`sm:hidden grid grid-cols-12 gap-1 px-2 py-2 text-sm hover:bg-gray-50 rounded ${selectedApparatus !== 'aa' && ranking.score && !ranking.score.didNotCompete && ranking.score.finalScore !== null ? 'cursor-pointer' : ''}`} onClick={() => handleGymnastClick(ranking, ranking.score)}>
                              <div className="col-span-2 flex items-center gap-1"><span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${getMedalStyle()}`}>{position}</span></div>
                              <div className="col-span-6"><div className="font-medium text-sm truncate">{ranking.name}</div><div className="text-xs text-gray-500 truncate">{ranking.club}</div></div>
                              <div className="col-span-4 text-right font-semibold">{getScoreDisplay(ranking)}</div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      {rankings.filter(r => !filterClub || r.club === filterClub).length === 0 && (
                        <p className="text-gray-500 text-center py-4">No rankings match the selected filter.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'scores' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-2">
                <h2 className="text-base sm:text-lg font-semibold text-gray-700">Latest Scores</h2>
                <p className="text-xs text-gray-500">Auto-refreshes every 5 seconds</p>
              </div>
              
              <div className="flex flex-wrap gap-3 mb-4">
                <select value={filterClub} onChange={(e) => setFilterClub(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">All Clubs</option>
                  {clubs.map((club) => (<option key={club.id} value={club.name}>{club.name}</option>))}
                </select>
                {filterClub && (<button onClick={() => setFilterClub('')} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline">Clear Filter</button>)}
              </div>
              
              {loadingScores && latestScores.length === 0 ? (
                <p className="text-gray-500">Loading scores...</p>
              ) : latestScores.length === 0 ? (
                <p className="text-gray-500">No scores yet. Scores will appear here as they are submitted.</p>
              ) : (
                <div className="space-y-2">
                  {latestScores.filter(score => !filterClub || score.clubName === filterClub).map((score, index) => (
                    <div key={score.id || index}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg cursor-pointer hover:ring-2 hover:ring-black transition-all ${index === 0 ? 'bg-green-50 border-l-4 border-l-green-500' : 'bg-gray-50 hover:bg-gray-100'}`}
                      onClick={() => {
                        setSelectedGymnastDetail({ gymnastNumber: score.gymnastNumber, gymnastName: score.gymnastName, clubName: score.clubName });
                        setSelectedScoreDetail(score);
                        setSelectedApparatusForModal(score.apparatus);
                        setScoreDetailOpen(true);
                      }}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="text-xl sm:text-2xl font-bold text-gray-700 w-10 sm:w-12 shrink-0">{score.gymnastNumber || '-'}</div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{score.gymnastName || 'Unknown'}</p>
                          <p className="text-xs sm:text-sm text-gray-500 truncate">{score.clubName || 'Unknown Club'}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 mt-2 sm:mt-0 pl-10 sm:pl-0">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          score.apparatus === 'vault' ? 'bg-purple-100 text-purple-800' :
                          score.apparatus === 'bars' ? 'bg-blue-100 text-blue-800' :
                          score.apparatus === 'beam' ? 'bg-pink-100 text-pink-800' :
                          score.apparatus === 'floor' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {score.apparatus?.toUpperCase() || '-'}
                        </span>
                        <div className="text-right">
                          <p className="text-xl sm:text-2xl font-bold">{parseFloat(score.finalScore || 0).toFixed(3)}</p>
                          <p className="text-xs text-gray-500">{score.createdAt ? new Date(score.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {latestScores.filter(score => !filterClub || score.clubName === filterClub).length === 0 && (
                    <p className="text-gray-500 text-center py-4">No scores match the selected filter.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </main>

        <footer>
          <div className="bg-white text-center py-3 border-t">
            <span className="text-sm text-gray-500">Coaches Live View</span>
            <span className="text-gray-400 mx-2">|</span>
            <span className="text-sm text-gray-600">&copy; 2025 - 2026</span>
          </div>
        </footer>

        <ScoreDetailModal isOpen={scoreDetailOpen} onClose={() => setScoreDetailOpen(false)} gymnast={selectedGymnastDetail} apparatus={selectedApparatusForModal || selectedApparatus} score={selectedScoreDetail} />
      </div>
    );
  }

  // Competition list view
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="bg-black text-white text-center py-1.5 sm:py-2 px-4 text-xs sm:text-sm font-medium">
        Coaches View - Live Scores & Rankings
      </div>
      
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <img src="/scorz-logo.png" alt="Scorz" className="h-8 sm:h-12" />
          <span className="text-xs sm:text-sm text-gray-500 font-medium">Coaches Access</span>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 w-full">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading events...</div></div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            <section>
              <button onClick={() => toggleSection('current')} className="flex items-center gap-2 text-xl sm:text-3xl font-light text-gray-700 mb-3 sm:mb-4 hover:text-gray-900 transition-colors">
                Current Events {expandedSections.current ? <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" /> : <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              {expandedSections.current && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {current.length === 0 ? <p className="text-gray-500 col-span-full">No current events</p> : current.map(comp => (
                    <Card key={comp.id} className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-l-4 border-l-gray-300" onClick={() => setSelectedEvent(comp)} data-testid={`coaches-event-${comp.id}`}>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-800 text-lg mb-1">{comp.title}</h3>
                        <div className="flex items-center gap-2 text-gray-600 text-sm mb-3"><MapPin className="w-4 h-4" /><span>{comp.publicVenue || comp.location || 'Venue TBC'}</span></div>
                        <div className="flex items-start gap-3 mt-4">
                          <img src={comp.organiserImage || "/scorz-logo.png"} alt="Organiser" className="w-12 h-12 object-contain" />
                          <div><p className="text-xs text-gray-500">Event Dates:</p><p className="text-sm text-gray-700">{comp.publicDates || `${formatDate(comp.startsOn || comp.startDate)} - ${formatDate(comp.endsOn || comp.endDate || comp.startsOn || comp.startDate)}`}</p></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>
            <section>
              <button onClick={() => toggleSection('future')} className="flex items-center gap-2 text-xl sm:text-3xl font-light text-gray-700 mb-3 sm:mb-4 hover:text-gray-900 transition-colors">
                Future Events {expandedSections.future ? <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" /> : <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              {expandedSections.future && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {future.length === 0 ? <p className="text-gray-500 col-span-full">No upcoming events</p> : future.map(comp => (
                    <Card key={comp.id} className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-l-4 border-l-gray-300" onClick={() => setSelectedEvent(comp)}>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-800 text-lg mb-1">{comp.title}</h3>
                        <div className="flex items-center gap-2 text-gray-600 text-sm"><MapPin className="w-4 h-4" /><span>{comp.publicVenue || comp.location || 'Venue TBC'}</span></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>
            <section>
              <button onClick={() => toggleSection('past')} className="flex items-center gap-2 text-xl sm:text-3xl font-light text-gray-700 mb-3 sm:mb-4 hover:text-gray-900 transition-colors">
                Past Events {expandedSections.past ? <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" /> : <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              {expandedSections.past && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {past.length === 0 ? <p className="text-gray-500 col-span-full">No past events</p> : past.map(comp => (
                    <Card key={comp.id} className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-l-4 border-l-gray-300" onClick={() => setSelectedEvent(comp)}>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-800 text-lg mb-1">{comp.title}</h3>
                        <div className="flex items-center gap-2 text-gray-600 text-sm"><MapPin className="w-4 h-4" /><span>{comp.publicVenue || comp.location || 'Venue TBC'}</span></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <footer>
        <div className="bg-white text-center py-3 border-t">
          <span className="text-sm text-gray-500">Coaches Live View</span>
          <span className="text-gray-400 mx-2">|</span>
          <span className="text-sm text-gray-600">&copy; 2025 - 2026</span>
        </div>
      </footer>
    </div>
  );
}
